import type { LaneDescriptor } from '@/features/chart/lib/profile-lane'
import type { VisibleRangeProfileBucket } from '@/features/chart/lib/visible-range-profile'
import type { TVChartApi, TVVisiblePriceRange } from '@/lib/tradingview/types/tv-widget.types'

export const TV_PROFILE_AXIS_GUTTER = 8
export const TV_PROFILE_LANE_GUTTER = 12
export const TV_PROFILE_BUCKET_GUTTER = 8

export type ProfileViewport = {
  width: number
  height: number
  priceRange: TVVisiblePriceRange
  inverted: boolean
  logarithmic: boolean
}

export type ProfileRectangle = {
  left: number
  right: number
  top: number
  bottom: number
  color: string
  lane: LaneDescriptor
  bucket: VisibleRangeProfileBucket
}

function laneBounds(width: number, count: number, index: number) {
  const laneWidth = Math.max(
    0,
    (width * 0.28 - TV_PROFILE_AXIS_GUTTER - TV_PROFILE_LANE_GUTTER * (count - 1)) / count
  )
  const right = width - TV_PROFILE_AXIS_GUTTER - index * (laneWidth + TV_PROFILE_LANE_GUTTER)
  return { left: right - laneWidth, right, width: laneWidth }
}

/** All geometry is in screen pixels. Candle times and the last loaded bar are irrelevant. */
export function buildProfileRectangles(
  lanes: readonly LaneDescriptor[],
  viewport: ProfileViewport
): ProfileRectangle[] {
  const { width, height, priceRange, inverted, logarithmic } = viewport
  if (lanes.length === 0 || width <= 0 || height <= 0) return []
  if (!(priceRange.to > priceRange.from) || (logarithmic && priceRange.from <= 0)) return []
  const transform = logarithmic ? Math.log : (value: number) => value
  const min = transform(priceRange.from)
  const max = transform(priceRange.to)
  const priceToY = (price: number) => {
    const ratio = (transform(price) - min) / (max - min)
    return (inverted ? ratio : 1 - ratio) * height
  }

  return lanes.flatMap((lane, index) => {
    if (lane.status !== 'ready') return []
    const bounds = laneBounds(width, lanes.length, index)
    const maximum = lane.buckets.reduce(
      (result, bucket) => Math.max(result, Math.abs(bucket.positive) + Math.abs(bucket.negative)),
      0
    )
    if (!(maximum > 0) || bounds.width === 0) return []
    return lane.buckets.flatMap((bucket) => {
      const low = Math.max(bucket.low, priceRange.from)
      const high = Math.min(bucket.high, priceRange.to)
      if (!(high > low)) return []
      const y1 = priceToY(low)
      const y2 = priceToY(high)
      const bucketHeight = Math.abs(y2 - y1)
      // Keep at least one pixel of a short bucket; sub-pixel buckets retain their full height.
      const inset = Math.min(TV_PROFILE_BUCKET_GUTTER / 2, Math.max(0, bucketHeight - 1) / 2)
      const top = Math.min(y1, y2) + inset
      const bottom = Math.max(y1, y2) - inset
      const negativeWidth = (Math.abs(bucket.negative) / maximum) * bounds.width
      const positiveWidth = (Math.abs(bucket.positive) / maximum) * bounds.width
      const segments = [
        [bounds.right - negativeWidth, bounds.right, lane.negativeColor],
        [
          bounds.right - negativeWidth - positiveWidth,
          bounds.right - negativeWidth,
          lane.positiveColor,
        ],
      ] as const
      return segments.flatMap(([left, right, color]) =>
        right > left ? [{ left, right, top, bottom, color, lane, bucket }] : []
      )
    })
  })
}

export function findProfileRectangle(
  rectangles: readonly ProfileRectangle[],
  x: number,
  y: number
) {
  return rectangles.find(
    (rectangle) =>
      x >= rectangle.left && x < rectangle.right && y >= rectangle.top && y < rectangle.bottom
  )
}

const FULL_NUMBER = new Intl.NumberFormat('en', { maximumFractionDigits: 8 })
const PROFILE_TITLES: Record<string, string> = {
  volume: 'Volume Delta Profile',
  liquidation: 'Liquidation Profile',
  openInterest: 'Open Interest Profile',
  liquidationLevels: 'Liquidation Levels',
  stopLevels: 'Stop Levels',
}

export function profileValueLines(lane: LaneDescriptor, bucket: VisibleRangeProfileBucket) {
  const format = FULL_NUMBER.format
  if (lane.volumeMode === 'total') return [`Total: ${format(bucket.positive)}`]
  if (lane.volumeMode === 'delta') return [`Delta: ${format(bucket.positive - bucket.negative)}`]
  return [
    `${lane.positiveLabel}: ${format(bucket.positive)}`,
    `${lane.negativeLabel}: ${format(bucket.negative)}`,
  ]
}

/**
 * Screen-anchored canvas, not a TV drawing: TV snaps future drawing timestamps to loaded bars.
 * The existing TV study still owns the indicator's settings and visibility. This renderer
 * never adds entities to the chart or saved layout. Pointer events pass through to TV.
 */
export function mountTVProfileCanvas(
  chart: TVChartApi,
  container: HTMLElement,
  lanes: readonly LaneDescriptor[]
) {
  const document = container.ownerDocument
  const view = document.defaultView
  if (!view) return () => {}
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) return () => {}
  canvas.setAttribute('aria-hidden', 'true')
  Object.assign(canvas.style, { position: 'absolute', pointerEvents: 'none', zIndex: '1' })
  const tooltip = document.createElement('div')
  tooltip.className = 'menu-surface'
  tooltip.setAttribute('role', 'tooltip')
  Object.assign(tooltip.style, {
    position: 'absolute',
    pointerEvents: 'none',
    zIndex: '2',
    display: 'none',
    padding: '8px 12px',
    fontSize: '12px',
    whiteSpace: 'pre',
  })
  container.append(canvas, tooltip)

  let disposed = false
  let frame: number | undefined
  let signature = ''
  let rectangles: ProfileRectangle[] = []
  let origin = { left: 0, top: 0 }
  let iframeDocument: Document | null = null
  let hoveredPointer: { x: number; y: number } | undefined

  const hide = () => {
    tooltip.style.display = 'none'
    hoveredPointer = undefined
  }
  const showHover = () => {
    if (!hoveredPointer) return
    const { x, y } = hoveredPointer
    const hit = findProfileRectangle(rectangles, x - origin.left, y - origin.top)
    if (!hit) {
      tooltip.style.display = 'none'
      return
    }
    tooltip.textContent = [
      PROFILE_TITLES[hit.lane.key] ?? hit.lane.key,
      `${FULL_NUMBER.format(hit.bucket.low)} – ${FULL_NUMBER.format(hit.bucket.high)}`,
      ...profileValueLines(hit.lane, hit.bucket),
    ].join('\n')
    tooltip.style.display = 'block'
    tooltip.style.left = `${Math.max(0, Math.min(x + 12, container.clientWidth - tooltip.offsetWidth))}px`
    tooltip.style.top = `${Math.max(0, Math.min(y + 12, container.clientHeight - tooltip.offsetHeight))}px`
  }
  const pointer = (event: PointerEvent) => {
    // Pointer coordinates are in the iframe viewport, not the parent's viewport.
    const iframe = container.querySelector('iframe')
    if (!iframe || event.buttons) {
      hide()
      schedule()
      return
    }
    const rootRect = container.getBoundingClientRect()
    const iframeRect = iframe.getBoundingClientRect()
    hoveredPointer = {
      x: event.clientX + iframeRect.left - rootRect.left,
      y: event.clientY + iframeRect.top - rootRect.top,
    }
    schedule()
  }
  const bindDocument = (next: Document | null) => {
    if (next === iframeDocument) return
    iframeDocument?.removeEventListener('pointermove', pointer, true)
    iframeDocument?.removeEventListener('pointerleave', hide)
    iframeDocument = next
    iframeDocument?.addEventListener('pointermove', pointer, true)
    iframeDocument?.addEventListener('pointerleave', hide)
  }

  const paint = () => {
    frame = undefined
    if (disposed) return
    try {
      const iframe = container.querySelector('iframe')
      bindDocument(iframe?.contentDocument ?? null)
      const area = iframeDocument?.querySelector<HTMLElement>('.layout__area--center')
      const panes = chart.getPanes()
      const pane = panes.find((candidate) => candidate.hasMainSeries?.()) ?? panes[0]
      const scale = pane?.getMainSourcePriceScale()
      const priceRange = scale?.getVisiblePriceRange()
      const height = pane?.getHeight?.()
      const width = chart.getTimeScale().width()
      if (!iframe || !area || !scale || !priceRange || !height || !(width > 0)) {
        canvas.style.display = 'none'
        rectangles = []
        signature = ''
        hide()
        return
      }
      const rootRect = container.getBoundingClientRect()
      const iframeRect = iframe.getBoundingClientRect()
      const areaRect = area.getBoundingClientRect()
      origin = {
        left: iframeRect.left - rootRect.left + areaRect.left,
        top: iframeRect.top - rootRect.top + areaRect.top,
      }
      const viewport: ProfileViewport = {
        width,
        height,
        priceRange,
        inverted: scale.isInverted(),
        logarithmic: scale.getMode() === 1,
      }
      const ratio = view.devicePixelRatio || 1
      const textColor = view.getComputedStyle(container).color
      const nextSignature = JSON.stringify({ ...viewport, origin, ratio, textColor })
      if (signature !== nextSignature) {
        signature = nextSignature
        rectangles = buildProfileRectangles(lanes, viewport)
        canvas.style.display = 'block'
        canvas.style.left = `${origin.left}px`
        canvas.style.top = `${origin.top}px`
        canvas.style.width = `${width}px`
        canvas.style.height = `${height}px`
        canvas.width = Math.ceil(width * ratio)
        canvas.height = Math.ceil(height * ratio)
        context.setTransform(ratio, 0, 0, ratio, 0, 0)
        context.clearRect(0, 0, width, height)
        for (const rectangle of rectangles) {
          context.fillStyle = rectangle.color
          context.globalAlpha = rectangle.bucket.isValueArea === false ? 0.35 : 1
          context.fillRect(
            rectangle.left,
            rectangle.top,
            rectangle.right - rectangle.left,
            rectangle.bottom - rectangle.top
          )
        }
        context.globalAlpha = 1
        context.fillStyle = textColor
        context.font = '10px sans-serif'
        context.textBaseline = 'middle'
        // Values appear only in the hover tooltip; retain loading/error/empty messages.
        lanes.forEach((lane, index) => {
          const bounds = laneBounds(width, lanes.length, index)
          if (!(bounds.width > 0)) return
          context.save()
          context.beginPath()
          context.rect(bounds.left, 0, bounds.width, height)
          context.clip()
          const message =
            lane.status === 'loading'
              ? 'Loading…'
              : lane.status === 'error'
                ? 'Failed to load'
                : lane.status === 'not-published'
                  ? (lane.notPublishedLabel ?? 'Not yet published')
                  : !lane.buckets.some((bucket) => bucket.positive !== 0 || bucket.negative !== 0)
                    ? lane.emptyLabel
                    : null
          if (message) {
            context.textAlign = 'center'
            context.fillText(message, (bounds.left + bounds.right) / 2, height / 2)
          }
          context.restore()
        })
      }
      showHover()
    } catch {
      canvas.style.display = 'none'
      rectangles = []
      signature = ''
      hide()
    }
  }
  function schedule() {
    if (!disposed && frame === undefined) frame = view!.requestAnimationFrame(paint)
  }
  const range = chart.onVisibleRangeChanged()
  const data = chart.onDataLoaded()
  const timeScale = chart.getTimeScale()
  const spacing = timeScale.barSpacingChanged()
  const offset = timeScale.rightOffsetChanged()
  range.subscribe(null, schedule)
  data.subscribe(null, schedule)
  spacing.subscribe(null, schedule)
  offset.subscribe(null, schedule)
  const resize = new view.ResizeObserver(schedule)
  resize.observe(container)
  // Public TV APIs expose no price-range subscription; catch vertical drags and scale modes.
  const poll = view.setInterval(schedule, 100)
  container.addEventListener('pointerleave', hide)
  paint()

  return () => {
    disposed = true
    if (frame !== undefined) view.cancelAnimationFrame(frame)
    view.clearInterval(poll)
    resize.disconnect()
    bindDocument(null)
    range.unsubscribe(null, schedule)
    data.unsubscribe(null, schedule)
    spacing.unsubscribe(null, schedule)
    offset.unsubscribe(null, schedule)
    container.removeEventListener('pointerleave', hide)
    canvas.remove()
    tooltip.remove()
  }
}
